<template>
  <div class="d-flex w-full flex-column h-screen bg-surface-secondary overflow-hidden">
    <vue-element-loading :active="loading" :is-full-screen="true" spinner="ring" color="#5c60f5" />

    <main class="flex-fill w-100 min-w-0 overflow-y-auto p-6 p-lg-10 custom-scrollbar pb-32">
      <div class="container-fluid px-0 w-100">
        <header class="mb-8 d-flex justify-content-between align-items-end">
          <div>
            <h1 class="h3 ls-tight mb-1">Genomic Analysis Pipeline</h1>
            <p class="text-sm text-muted">Follow the steps below to configure your high-performance genomic computing task.</p>
          </div>
          <div class="hstack gap-3">
            <div class="d-none d-md-flex flex-column align-items-end me-3">
              <span class="text-2xs text-uppercase font-bold text-muted">Cluster Status</span>
              <span class="text-xs font-bold text-success"><i class="bi bi-cpu-fill me-1"></i>Nodes Online</span>
            </div>
            <router-link class="btn btn-sm btn-neutral" to="/tutorial">
              <i class="bi bi-question-circle me-2"></i>Guide
            </router-link>
          </div>
        </header>

        <div class="vstack gap-8">
          <!-- Step 01 -->
          <StepDataIngestion
            :gwasKeyword="gwasKeyword"
            :loadingGwas="loadingGwas"
            :gwasError="gwasError"
            :gwasFiles="gwasFiles"
            :gwasFileId="gwasFileId"
            :gwasSelectedLabel="gwasSelectedLabel"
            :gwasTotal="gwasTotal"
            :gwasPage="gwasPage"
            :gwasMaxPage="gwasMaxPage"
            :ldKeyword="ldKeyword"
            :loadingLd="loadingLd"
            :ldError="ldError"
            :ldFiles="ldFiles"
            :fileLDId="fileLDId"
            :ldSelectedLabel="ldSelectedLabel"
            :ldTotal="ldTotal"
            :ldPage="ldPage"
            :ldMaxPage="ldMaxPage"
            @update:gwasKeyword="gwasKeyword = $event"
            @update:ldKeyword="ldKeyword = $event"
            @refresh-gwas="fetchGwasFiles"
            @refresh-ld="fetchLdFiles"
            @gwas-prev="gwasPrev"
            @gwas-next="gwasNext"
            @ld-prev="ldPrev"
            @ld-next="ldNext"
            @select-gwas="selectGwas"
            @select-ld="selectLd"
          />

          <div class="pipeline-flow-line"></div>

          <!-- Step 02 -->
          <section class="pipeline-section">
            <div class="d-flex align-items-center gap-3 mb-4">
              <span class="step-indicator">02</span>
              <div>
                <h2 class="h5 mb-0">Algorithm Selection</h2>
                <p class="text-2xs text-muted mb-0">Enable modules to configure them below.</p>
              </div>
            </div>

            <div v-if="algoError" class="alert alert-danger py-2 text-sm">
              <i class="bi bi-exclamation-triangle me-1"></i>{{ algoError }}
            </div>

            <div class="border rounded-3 bg-white shadow-sm overflow-hidden">
              <table class="table table-hover table-borderless align-middle mb-0">
                <thead class="bg-surface-tertiary border-bottom">
                  <tr>
                    <th class="ps-6 py-3 text-2xs text-uppercase text-muted font-bold" style="width: 80px;">Enable</th>
                    <th class="py-3 text-2xs text-uppercase text-muted font-bold">Module Name</th>
                    <th class="py-3 text-2xs text-uppercase text-muted font-bold">Core Capability</th>
                    <th class="py-3 text-2xs text-uppercase text-muted font-bold text-end pe-6">Type</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="algo in algorithms.algorithmsShow"
                    :key="algo.id"
                    :class="{ 'bg-blue-50': isAlgoSelected(algo.id) }"
                  >
                    <td class="ps-6">
                      <div class="form-check form-switch">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          :checked="isAlgoSelected(algo.id)"
                          @change="toggleAlgorithm($event, algo)"
                        >
                      </div>
                    </td>
                    <td>
                      <div class="d-flex align-items-center">
                        <span class="font-bold text-dark me-2">{{ algo.name }}</span>
                        <code class="text-2xs text-primary bg-soft-primary px-1 rounded">{{ algo.code }}</code>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs text-muted mb-0 text-truncate" style="max-width: 400px;">
                        {{ algo.description }}
                      </p>
                    </td>
                    <td class="text-end pe-6">
                      <span class="badge badge-dot">
                        <i class="bg-primary"></i><span class="text-2xs text-muted ms-2">Core</span>
                      </span>
                    </td>
                  </tr>

                  <tr v-if="!algorithms.algorithmsShow || algorithms.algorithmsShow.length === 0">
                    <td colspan="4" class="py-4 text-center text-muted">
                      No algorithms loaded. Check PocketBase collection <code>algorithms</code>.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </main>

    <!-- Bottom Parameter Panel -->
    <BottomParameterPanel
      :algorithmsData="algorithmsData"
      :activeAlgoId="activeAlgoId"
      :jobName="jobName"
      :submitFlag="submitFlag"
      :submitDisabledReason="submitDisabledReason"
      :currentAlgoParams="currentAlgoParams"
      :cromwellInputPreview="cromwellInputPreview"
      :isModifiedFn="isModified"
      :isParamTouchedFn="isParamTouched"
      :stringifyDefaultFn="stringifyDefault"
      :prettyJsonFn="prettyJson"
      @update:activeAlgoId="activeAlgoId = $event"
      @update:jobName="jobName = $event"
      @save-params="onSaveParameters"
      @submit="dataSubmit"
    />
  </div>
</template>

<script>
import { isEmpty } from "@/utils/validate";
import { pb } from "@/utils/pocketbase";
import { listFiles } from "@/api/storage";

import StepDataIngestion from "@/components/pipeline/StepDataIngestion.vue";
import BottomParameterPanel from "@/components/pipeline/BottomParameterPanel.vue";

import {
  parseJsonLike,
  statusBadgeClass,
  loadFileList,
  applyStep1MappingToAlgo,
  normalizeValueForCromwell,
  prettyJson,
  stringifyDefault,
} from "@/utils/pipelineHelpers";

export default {
  name: "AnalysisItem",
  components: { StepDataIngestion, BottomParameterPanel },

  data() {
    return {
      loading: false,
      algoError: "",

      algorithms: { algorithmsShow: [] },
      activeAlgoId: null,

      // Step1 selections
      gwasFileId: null,
      fileLDId: null,
      gwasFileKey: null,
      ldFileKey: null,

      // ✅ LD 来源与官方路径
      ldSource: "uploaded",     // uploaded | official
      officialLdPath: null,     // 例如 /pmaster/.../LD_reference/EUR

      jobName: `ANALYSIS_${new Date().toISOString().slice(0, 10).replace(/-/g, "_")}`,

      // Step1 lists
      gwasFiles: [],
      gwasTotal: 0,
      gwasPage: 1,
      gwasPageSize: 6,
      gwasKeyword: "",
      loadingGwas: false,
      gwasError: "",
      gwasSelectedLabel: "",

      ldFiles: [],
      ldTotal: 0,
      ldPage: 1,
      ldPageSize: 6,
      ldKeyword: "",
      loadingLd: false,
      ldError: "",
      ldSelectedLabel: "",
    };
  },

  computed: {
    algorithmsData() {
      return this.$store.getters["algorithmsData/getAlgorithmsData"];
    },

    currentAlgoParams() {
      if (!this.activeAlgoId) return [];
      const algo = this.algorithms.algorithmsShow.find((a) => a.id === this.activeAlgoId);
      return algo ? algo.parameters : [];
    },

    submitFlag() {
      const ldOk =
        (this.ldSource === "official" && !!this.officialLdPath) ||
        (this.ldSource === "uploaded" && !!this.fileLDId && !!this.ldFileKey);

      return (
        this.loading ||
        this.algorithmsData.length === 0 ||
        !this.gwasFileId ||
        !this.gwasFileKey ||
        !ldOk ||
        isEmpty(this.jobName)
      );
    },

    submitDisabledReason() {
      if (this.loading) return "Submitting...";
      if (this.algorithmsData.length === 0) return "Please enable at least one algorithm";
      if (!this.gwasFileId) return "Please select a GWAS file";
      if (!this.gwasFileKey) return "GWAS key not resolved (files.key empty?)";

      if (this.ldSource === "official") {
        if (!this.officialLdPath) return "Please select an official LD panel";
      } else {
        if (!this.fileLDId) return "Please select an LD reference";
        if (!this.ldFileKey) return "LD key not resolved (files.key empty?)";
      }

      if (isEmpty(this.jobName)) return "Job ID is empty";
      return "";
    },

    cromwellInputPreview() {
      return this.buildCromwellInputsPreview();
    },

    gwasMaxPage() {
      return Math.max(1, Math.ceil((this.gwasTotal || 0) / this.gwasPageSize));
    },

    ldMaxPage() {
      return Math.max(1, Math.ceil((this.ldTotal || 0) / this.ldPageSize));
    },
  },

  watch: {
    // ✅ 任何 Step1 关键字段变化，都强制把 active algo 的参数刷新一次（让 Bottom 立刻变）
    gwasFileKey() { this.refreshActiveAlgoParamsFromStep1(); },
    ldSource() { this.refreshActiveAlgoParamsFromStep1(); },
    ldFileKey() { this.refreshActiveAlgoParamsFromStep1(); },
    officialLdPath() { this.refreshActiveAlgoParamsFromStep1(); },
    activeAlgoId() { this.refreshActiveAlgoParamsFromStep1(); },
  },

  methods: {
    statusBadgeClass,

    // -----------------------------
    // Step1: effective LD key
    // -----------------------------
    getEffectiveLdKey() {
      return (this.ldSource === "official") ? this.officialLdPath : this.ldFileKey;
    },

    // ✅ 强制把 Step1 的 GWAS/LD 写入「算法参数对象本身」
    // 这样 Bottom 如果直接渲染 currentAlgoParams，一定会立刻更新
    forceApplyStep1ToAlgorithm(algo) {
      if (!algo || !Array.isArray(algo.parameters)) return;

      const effectiveLd = this.getEffectiveLdKey();
      const effectiveGwas = this.gwasFileKey;

      // 按你 WDL 示例的真实入参 key
      const LD_KEYS = ["PandT.ldref_0"];
      const GWAS_KEYS = ["PandT.summary_statistic_2"];

      algo.parameters.forEach((p) => {
        if (!p || !p.wdlKey) return;

        if (LD_KEYS.includes(p.wdlKey) && effectiveLd) {
          // Vue2 深层属性：用 $set 保证响应式
          this.$set(p, "defaultValue", effectiveLd);
          this.$set(p, "setValue", effectiveLd);
        }

        if (GWAS_KEYS.includes(p.wdlKey) && effectiveGwas) {
          this.$set(p, "defaultValue", effectiveGwas);
          this.$set(p, "setValue", effectiveGwas);
        }
      });
    },

    refreshActiveAlgoParamsFromStep1() {
      if (!this.activeAlgoId) return;
      const algo = this.algorithms.algorithmsShow.find((a) => a.id === this.activeAlgoId);
      if (!algo) return;
      this.forceApplyStep1ToAlgorithm(algo);
    },

    // -----------------------------
    // Step1: load GWAS/LD list
    // -----------------------------
    fetchGwasFiles() {
      return loadFileList({
        listFiles,
        category: "gwas",
        page: this.gwasPage,
        pageSize: this.gwasPageSize,
        keyword: this.gwasKeyword,
        setLoading: (v) => (this.loadingGwas = v),
        setError: (m) => (this.gwasError = m),
        setList: (arr) => (this.gwasFiles = arr),
        setTotal: (n) => (this.gwasTotal = n),
      });
    },

    fetchLdFiles() {
      return loadFileList({
        listFiles,
        category: "LD",
        page: this.ldPage,
        pageSize: this.ldPageSize,
        keyword: this.ldKeyword,
        setLoading: (v) => (this.loadingLd = v),
        setError: (m) => (this.ldError = m),
        setList: (arr) => (this.ldFiles = arr),
        setTotal: (n) => (this.ldTotal = n),
      });
    },

    async turnPage(type, delta) {
      const isGwas = type === "gwas";
      const pageKey = isGwas ? "gwasPage" : "ldPage";
      const maxPage = isGwas ? this.gwasMaxPage : this.ldMaxPage;

      const next = this[pageKey] + delta;
      if (next < 1 || next > maxPage) return;
      this[pageKey] = next;

      if (isGwas) return this.fetchGwasFiles();
      return this.fetchLdFiles();
    },

    gwasPrev() { return this.turnPage("gwas", -1); },
    gwasNext() { return this.turnPage("gwas", +1); },
    ldPrev() { return this.turnPage("ld", -1); },
    ldNext() { return this.turnPage("ld", +1); },

    // -----------------------------
    // Step1: selection
    // -----------------------------
    selectFile(type, f) {
      const label = `${f.display_name} (${f.ancestry || "NA"})`;
      const key = f.storage_key || null;

      if (type === "gwas") {
        this.gwasFileId = f.file_id;
        this.gwasSelectedLabel = label;
        this.gwasFileKey = key;
      } else {
        // ✅ 上传 LD：强制切回 uploaded
        this.ldSource = "uploaded";
        this.officialLdPath = null;

        this.fileLDId = f.file_id;
        this.ldSelectedLabel = label;
        this.ldFileKey = key;
      }

      // ✅ 关键：先刷新 active algo（Bottom 立即变化）
      this.refreshActiveAlgoParamsFromStep1();

      // ✅ 再同步 store（用于提交）
      (this.algorithmsData || []).forEach((m) => this.onSaveParameters(m.id));
    },

    selectGwas(f) { this.selectFile("gwas", f); },

    // ✅ official / uploaded LD 都走这里
    selectLd(payload) {
      // official: { source:"official", population:"EUR", ldref_0:"/pmaster/.../EUR", display_name:"Official EUR" }
      if (payload && payload.source === "official") {
        this.ldSource = "official";
        this.officialLdPath = payload.ldref_0;
        this.ldSelectedLabel = payload.display_name || `Official ${payload.population || ""}`.trim();

        // 清理上传标记
        this.fileLDId = "__official__";
        this.ldFileKey = null;

        // ✅ 刷新 active algo（Bottom 立即变化）
        this.refreshActiveAlgoParamsFromStep1();

        // ✅ 同步 store
        (this.algorithmsData || []).forEach((m) => this.onSaveParameters(m.id));
        return;
      }

      // uploaded
      this.selectFile("ld", payload);
    },

    // -----------------------------
    // Step2: algorithms from PB
    // -----------------------------
    async fetchAlgorithmsFromPB() {
      try {
        this.loading = true;
        this.algoError = "";

        const list = await pb.collection("algorithms").getFullList({
          sort: "name",
          filter: `status = "active" && type = "single" `,
        });

        this.algorithms.algorithmsShow = list.map((a) => {
          const schema = parseJsonLike(a.input_schema, {});
          const defaults = parseJsonLike(a.defaults, {});

          const parameters =
            schema && schema.properties
              ? this.buildParamsFromJsonSchema(a, schema, defaults)
              : this.buildParamsFromFlatCromwellConfig(a, schema);

          return {
            id: a.id,
            code: a.code?.toLowerCase(),
            name: a.name,
            description: a.description || "",
            parameters,
          };
        });

        // ✅ 加载完算法后，如果 Step1 已选，立刻写入 active algo（避免第一次不显示）
        this.refreshActiveAlgoParamsFromStep1();

      } catch (e) {
        console.error("[Error] fetchAlgorithmsFromPB:", e);
        this.algoError = e?.message || "Failed to load algorithms";
      } finally {
        this.loading = false;
      }
    },

    isAlgoSelected(id) {
      return this.algorithmsData.some((a) => a.id === id);
    },

    isParamTouched(p) {
      if (p.isArray) return Array.isArray(p.setValue) && p.setValue.length > 0;
      return p.setValue !== "" && p.setValue !== null && p.setValue !== undefined;
    },

    isModified(algoId) {
      const algo = this.algorithms.algorithmsShow.find((a) => a.id === algoId);
      return !!algo && algo.parameters.some((p) => this.isParamTouched(p));
    },

    toggleAlgorithm(e, algo) {
      if (e.target.checked) {
        this.activeAlgoId = algo.id;

        // ✅ 勾选时：先把 Step1 写进该算法参数对象（Bottom 立即更新）
        this.forceApplyStep1ToAlgorithm(algo);

        // 再落 store
        this.onSaveParameters(algo.id);
      } else {
        this.$store.dispatch("algorithmsData/deleteAlgorithmsData", algo.id);
        if (this.activeAlgoId === algo.id) {
          this.activeAlgoId = this.algorithmsData.length > 0 ? this.algorithmsData[0].id : null;
        }
      }
    },

    // -----------------------------
    // schema to params
    // -----------------------------
    buildParamsFromJsonSchema(a, schema, defaults) {
      const props = schema.properties || {};
      const requiredList = Array.isArray(schema.required) ? schema.required : [];

      return Object.keys(props).map((key, idx) => {
        const def = defaults?.[key] ?? props[key]?.default ?? "";
        const propType = props[key]?.type || "";
        const enumList = Array.isArray(props[key]?.enum) ? props[key].enum : [];
        const isArray = propType === "array" || Array.isArray(def);

        return {
          id: `${a.id}_${idx}_${key}`,
          wdlKey: key,
          displayName: key,
          description: props[key]?.description || "",
          defaultValue: def,
          setValue: isArray ? [] : "",
          schemaType: propType || (isArray ? "array" : "string"),
          isArray,
          enum: enumList,
          required: requiredList.includes(key),
        };
      });
    },

    buildParamsFromFlatCromwellConfig(a, cfg) {
      const parameters = [];
      const keys = Object.keys(cfg || {});
      const isMetaKey = (k) => k.endsWith("_description") || k.endsWith("_type");

      keys.forEach((k) => {
        if (!k.endsWith("_value")) return;

        const base = k.slice(0, -"_value".length);
        const desc = cfg[`${base}_description`] || "";
        const typ = String(cfg[`${base}_type`] || "").toLowerCase();

        const rawVal = cfg[k];
        const isArray = Array.isArray(rawVal);
        const enumList = isArray ? rawVal : [];

        let schemaType = "string";
        if (typ === "float" || typ === "number") schemaType = "number";
        if (typ === "int" || typ === "integer") schemaType = "integer";
        if (typ === "boolean" || typ === "bool") schemaType = "boolean";
        if (isArray) schemaType = "array";

        parameters.push({
          id: `${a.id}_${base}`,
          wdlKey: k,
          displayName: base.split(".").pop(),
          description: desc,
          defaultValue: rawVal,
          setValue: isArray ? [] : "",
          schemaType,
          isArray,
          enum: enumList,
          required: false,
        });
      });

      keys.forEach((k) => {
        if (k.endsWith("_value")) return;
        // if (isMetaKey(k)) return;
        if (parameters.some((p) => p.wdlKey === k)) return;

        const rawVal = cfg[k];
        const isArray = Array.isArray(rawVal);

        let schemaType = "string";
        if (typeof rawVal === "number") schemaType = "number";
        if (typeof rawVal === "boolean") schemaType = "boolean";
        if (isArray) schemaType = "array";

        parameters.push({
          id: `${a.id}_${k}`,
          wdlKey: k,
          displayName: k.split(".").pop(),
          description: "",
          defaultValue: rawVal,
          setValue: isArray ? [] : "",
          schemaType,
          isArray,
          enum: [],
          required: false,
        });
      });

      parameters.sort((x, y) => x.wdlKey.localeCompare(y.wdlKey));
      return parameters;
    },

    // -----------------------------
    // Cromwell input build
    // -----------------------------
    onSaveParameters(algoId) {
      const algorithm = this.algorithms.algorithmsShow.find((a) => a.id === algoId);
      if (!algorithm) return;

      // ✅ 关键：effective LD
      const effectiveLd = this.getEffectiveLdKey();

      // ✅ 同时把 Step1 写进「参数对象」本身，保证 UI/preview 也一致
      this.forceApplyStep1ToAlgorithm(algorithm);

      applyStep1MappingToAlgo({
        algo: algorithm,
        gwasKey: this.gwasFileKey,
        ldKey: effectiveLd,
      });

      const kvParams = {};
      algorithm.parameters.forEach((p) => {
        const finalVal = p.isArray
          ? ((Array.isArray(p.setValue) && p.setValue.length > 0) ? p.setValue : p.defaultValue)
          : ((p.setValue === "" || p.setValue === null || p.setValue === undefined) ? p.defaultValue : p.setValue);

        kvParams[p.wdlKey] = normalizeValueForCromwell(p, finalVal);
      });

      this.$store.dispatch("algorithmsData/addAlgorithmsData", {
        id: algorithm.id,
        code: algorithm.code,
        name: algorithm.name,
        parameters: kvParams,
      });
    },

    buildCromwellInputsPreview() {
      const merged = {};
      (this.algorithmsData || []).forEach((m) => {
        if (!m?.parameters) return;
        Object.keys(m.parameters).forEach((k) => (merged[k] = m.parameters[k]));
      });
      return merged;
    },

    prettyJson,
    stringifyDefault,

    // -----------------------------
    // Submit
    // -----------------------------
    async dataSubmit() {
  if (!pb.authStore.isValid) {
    this.$message?.error?.("Please login first.");
    return;
  }

  try {
    this.loading = true;

    // ✅ 1) 先对当前选择的 algorithmsData 做“快照”
    //    避免后续 onSaveParameters() 改写 store 导致列表变了
    const selected = (this.algorithmsData || []).map((m) => ({
      id: m.id,
      code: m.code,
      name: m.name,
      parameters: { ...(m.parameters || {}) },
    }));

    if (selected.length === 0) {
      this.$message?.error?.("Please enable at least one algorithm");
      return;
    }

    // ✅ 2) 再刷新 Step1 mapping（允许你继续用原逻辑）
    //    注意：这一步可能会改 store，但不会影响 selected 快照
    selected.forEach((m) => this.onSaveParameters(m.id));

    // ✅ 3) 再从 store 里取一次最新参数（可选，但建议）
    //    如果你希望提交的一定是“刷新后的最终参数”，就用 refreshedMap 覆盖 selected.parameters
    const refreshedMap = new Map(
      (this.algorithmsData || []).map((x) => [String(x.id), { ...(x.parameters || {}) }])
    );

    const owner = pb.authStore.model.id;
    const created = [];
    const submitted = [];

    const baseUrl = (pb.baseUrl || "http://192.168.77.22:8090").replace(/\/+$/, "");
    const token = pb.authStore.token;

    for (const m of selected) {
      // ✅ 使用刷新后的参数（如果没取到就退回快照参数）
      const params = refreshedMap.get(String(m.id)) || m.parameters || {};
      const inputs_json = { ...params };

      const payload = {
        owner,
        algorithm: m.id,
        params,
        inputs_json,

        engine: "cromwell",
        status: "draft",

        options_json: {
          workflow_failure_mode: "ContinueWhilePossible",
          enable_ssh_access: true,
          write_to_cache: true,
          read_from_cache: false,
          memory_retry_multiplier: 2,
          delete_intermediate_output_files: true,
          final_workflow_outputs_dir: "xxx",
          use_relative_output_paths: true,
          final_workflow_log_dir: "xxx/wf_logs",
          final_call_logs_dir: "xxx/call_logs",
          default_runtime_attributes: { slurm_partition: "debug" },
        },

        ...(this.cromwellServer ? { cromwell_server: this.cromwellServer } : {}),
      };

      // ✅ create
      const rec = await pb.collection("runs").create(payload);
      created.push(rec);

      // ✅ submit
      const runId = rec.id;
      const submitUrl = `${baseUrl}/api/runs/${runId}/submit`;

      let resp;
      try {
        resp = await fetch(submitUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        });
      } catch (e) {
        console.error("[Submit fetch error]", runId, e);
        this.$message?.error?.(`Run ${runId} submit fetch error: ${e?.message || e}`);
        continue;
      }

      const out = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        console.error("[Submit API failed]", runId, out);
        this.$message?.error?.(`Run ${runId} submit failed: ${out?.message || resp.status}`);
        continue;
      }

      submitted.push({ runId, ...out });
    }

    this.$message?.success?.(
      `Created ${created.length} run(s), submitted ${submitted.length} run(s)!`
    );
    this.$router.push({ name: "statistics" });
    return { created, submitted };

  } catch (e) {
    console.error("[Error] submit failed:", e);
    this.$message?.error?.(e?.message || "Submit failed");
  } finally {
    this.loading = false;
  }
},
  },

  async mounted() {
    this.$store.dispatch("algorithmsData/deleteAllAlgorithmsData");
    await Promise.all([this.fetchGwasFiles(), this.fetchLdFiles()]);
    await this.fetchAlgorithmsFromPB();
  },
};
</script>

<style scoped>
.step-indicator {
  width: 28px; height: 28px;
  display: flex; align-items: center; justify-content: center;
  background-color: #5c60f5; color: #fff;
  border-radius: 6px; font-weight: 800; font-size: 0.7rem;
}
.pipeline-flow-line {
  height: 30px;
  width: 2px;
  border-left: 2px dashed #dee2e6;
  margin-left: 13px;
}
.custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 10px; }

.bg-soft-primary { background: rgba(92, 96, 245, 0.12); }
.full-page {
  width: 100% !important;
  max-width: 100% !important;
}
.full-page, .full-page * { min-width: 0; }
main { display: flex; flex-direction: column; }
</style>